// =============================================================================
// components/settings/primitives.js — Phase 82 (Settings sidebar redesign)
//
// Shared visual primitives used across every settings section. Ported from
// the Figma export and normalised for:
//   • Dark mode — no hard-coded #hex colors on structural elements. Uses
//     token classes (bg-card, text-charcoal, bg-offwhite, border-border,
//     text-muted-foreground) so light↔dark themes swap automatically.
//   • Accessibility — buttons have role/aria attributes, focus rings.
//   • Testability — every interactive element accepts a data-testid.
// =============================================================================
import React from 'react';

export function SectionHeader({ title, subtitle, testId }) {
  return (
    <div className="mb-7" data-testid={testId}>
      <h2
        className="font-display text-xl font-medium text-charcoal"
        style={{ letterSpacing: '-0.01em' }}
      >
        {title}
      </h2>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  );
}

export function SettingsCard({ children, className = '', style, testId }) {
  return (
    <div
      className={`bg-card rounded-2xl border border-border ${className}`}
      style={style}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export function Toggle({ checked, onChange, disabled = false, testId, ariaLabel }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      data-testid={testId}
      className="relative flex-shrink-0 transition-colors focus:outline-none focus:ring-2 focus:ring-evergreen/30 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        width: 40,
        height: 22,
        backgroundColor: checked ? 'hsl(var(--evergreen))' : 'hsl(var(--border))',
      }}
    >
      <span
        className="absolute top-0.5 rounded-full bg-white transition-all"
        style={{
          width: 18,
          height: 18,
          left: checked ? 20 : 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  );
}

export function FieldLabel({ label, hint, htmlFor }) {
  return (
    <div className="mb-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-charcoal">
        {label}
      </label>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

export function TextInput({ value, onChange, placeholder, type = 'text', disabled = false, testId }) {
  return (
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      data-testid={testId}
      className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-offwhite text-sm text-charcoal placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-evergreen/20 disabled:opacity-60 transition"
    />
  );
}

export function SelectInput({ value, onChange, options, disabled = false, testId }) {
  // Note: This is a native <select>. The Figma design shows a caret via
  // background-image, kept here for visual parity. All shadcn Selects would
  // work too but the native element is smaller and universally accessible.
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={testId}
      className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-offwhite text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-evergreen/20 transition appearance-none disabled:opacity-60"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239B9890' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * PrimaryButton — three tonal variants matched to the Figma spec.
 *   • primary — filled evergreen (main call to action).
 *   • ghost   — outlined evergreen (secondary actions).
 *   • danger  — outlined red (destructive intent).
 */
export function PrimaryButton({
  children, onClick, variant = 'primary', size = 'md', disabled = false, testId, type = 'button',
}) {
  const base = 'rounded-xl font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';
  const sizes = { sm: 'px-3.5 py-1.5 text-xs', md: 'px-5 py-2.5 text-sm' };
  const variants = {
    primary: 'bg-evergreen text-white hover:bg-evergreen-dark focus:ring-evergreen/40',
    ghost:   'bg-transparent text-evergreen border border-evergreen/30 hover:bg-evergreen/5 focus:ring-evergreen/30',
    danger:  'bg-transparent border text-red-600 dark:text-red-400 border-red-600/30 dark:border-red-400/30 hover:bg-red-500/5 focus:ring-red-500/30',
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`${base} ${sizes[size]} ${variants[variant]}`}
    >
      {children}
    </button>
  );
}

export function SavedFlash({ visible, label = 'Saved' }) {
  // v3.7.11 Phase 84.31 Part 5 — Announce autosave to screen readers.
  // The visual chip is still gated on `visible`, but the aria-live
  // region remains in the DOM so assistive tech can read the change.
  return (
    <span
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={
        'text-sm flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 transition-opacity ' +
        (visible ? 'opacity-100' : 'opacity-0 pointer-events-none')
      }
    >
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="2,6.5 5,9.5 11,3" />
      </svg>
      {visible ? label : ''}
    </span>
  );
}

/**
 * PillChoice — the multi-select chip pattern used across Life Profile and the
 * Region section. Colors adapt to selection state; both colors are token-based.
 */
export function PillChoice({ children, active, onClick, tone = 'evergreen', testId }) {
  const tones = {
    evergreen: active
      ? 'bg-evergreen text-white border-evergreen'
      : 'bg-transparent text-muted-foreground border-border hover:border-evergreen/40',
    danger: active
      ? 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30'
      : 'bg-transparent text-muted-foreground border-border hover:border-red-500/30',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3.5 py-2 rounded-xl text-sm font-medium border transition-all ${tones[tone]}`}
    >
      {children}
    </button>
  );
}
